import React, { useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { inject, observer } from "mobx-react";
import { debounce } from "lodash";
import {
  AccountSettingsStore,
  DonationStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../store";
import {  DonationService } from "../services";
import {
  ActiveInactiveButton,
  AuthService,
  ITableHeaders,
  TableHeader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { DonationGridModel } from "../models";
import {
  downloadBlob,
  exportFileName,
  isUsableExportFile,
  showExportError,
  ExportFormat,
} from "../utils";
import ExportDropDown from "./ExportDropDown";
import DashboardTopRightSide from "./DashboardTopRightSide";
import DashboardTopBar from "./DashboardTopBar";
import ItemContentArea from "./ItemContentArea";
import { GridNames, ItemDropDownOptions } from "../enums";
import TableGrid from "./TableGrid";
import { MerchantAccountStore } from "../../modules";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  navigate: NavigateFunction;
  donationStore: DonationStore;
  itemStore: ItemsStore;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const Grid: React.FC<IProps> = ({
  donationStore,
  itemStore,
  navigate,
  merchantAccountStore,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);
  const [isExporting, setIsExporting] = React.useState(false);

  /* Exports every donation, not just the page on screen: the API builds the file from the same
     query the grid uses, minus pagination, honouring the active search. */
  function exportAll(format: ExportFormat) {
    if (isExporting) return;
    setIsExporting(true);
    DonationService.exportDonations(
      AuthService.authData.accountId,
      format,
      itemStore.searchValue
    ).subscribe({
      next: (blob) => {
        // Cleared before the download so a browser that refuses it cannot strand the button.
        setIsExporting(false);
        if (!isUsableExportFile(blob)) {
          ToastService.showError("Error in exporting donations");
          return;
        }
        downloadBlob(blob, exportFileName("donations", format));
      },
      error: (error) => {
        setIsExporting(false);
        showExportError(error, "Error in exporting donations");
      },
    });
  }

  useEffect(() => {
    itemStore.updateSearchValue("", ItemDropDownOptions.Donations);
    loadData(
      itemStore.donationGridPagination.pageNumber,
      itemStore.donationGridPagination.pageSize,
      itemStore.searchValue,
      true
    );

    const subscription: Subscription = itemStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        loadData(
          itemStore.donationGridPagination.pageNumber,
          itemStore.donationGridPagination.pageSize,
          itemStore.searchValue
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    DonationService.getDonations(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        itemStore.deserializeDonationGridData(
          response.result || [],
          response.meta
        );
      },
    });
  }

  function edit(id: string) {
    donationStore?.reset();
    navigate(`/app/donation/detail/${id}`);
  }

  const debounceSearch = debounce(
    () =>
      loadData(
        itemStore.donationGridPagination.pageNumber,
        itemStore.donationGridPagination.pageSize,
        itemStore.searchValue
      ),
    500
  );

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, itemStore.searchValue);
  }

  const isNoData =
    firstTimeLoad &&
    itemStore.donationGridPagination.totalItemCount === 0 &&
    !showLoader &&
    itemStore.searchValue === "";

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
        extraContent={
          !isNoData && (
            <ExportDropDown onExport={exportAll} isExporting={isExporting} />
          )
        }
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function columns() {
    const columns: ITableHeaders<DonationGridModel>[] = [
      {
        dataKey: "success",
        title: "Status",
        onCellRender: (data: DonationGridModel) => {
          const title = data.isRefund
            ? data.success
              ? "Refund"
              : "Refund Failed"
            : data.success
              ? "Success"
              : "Failed";
          return (
            <ActiveInactiveButton
              inActive={!data.success}
              // fontSize="12px" fontWeight="600" minWidth="80px"
            >
              {title}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "createdDate",
        title: "Date",
        className: "table-col-20",
        cellClassName: "table-col-20 clickable",
        onCellRender: (data: DonationGridModel) => {
          return UtilityMethods.formatDateInStringWithTime(data.createdDate);
        },
      },
      {
        dataKey: "amount",
        title: "Amount",
        // Right-aligned like every other money column in the app (payments, orders, invoices).
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: DonationGridModel) => {
          return data.isRefund
            ? `($${AccountSettingsStore.formatAmount(data.amount)})`
            : `$${AccountSettingsStore.formatAmount(data.amount)}`;
        },
      },
      {
        dataKey: "contact",
        title: "Contact",
        cellClassName: "clickable",
        cellColor: "#556bb8",
        onCellClick: (
          e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
          data: DonationGridModel
        ) => {
          if (!data.contact?.id) return;
          navigate(`/app/contacts/detail/${data.contact?.id}`);
        },
        onCellRender: (data: DonationGridModel) => {
          return data.contact?.fullName || data.contact?.email || "";
        },
      },
      {
        dataKey: "name",
        title: "Donation Name",
        className: "table-col-30",
        cellClassName: "table-col-30 clickable",
        onCellRender: (data: DonationGridModel) => {
          return <span onClick={() => edit(data.id)}>{data.name}</span>;
        },
      },
      {
        dataKey: "donationPaidTo",
        title: "Beneficiary",
        className: "table-col-20",
        cellClassName: "table-col-20",
      },
    ];
    return columns;
  }

  return renderDetails(
    <TableGrid
      tableHeader={
        <TableHeader
          showFilter={itemStore.showItemsFilter}
          onToggleFilter={() => itemStore.toggleItemsFilter()}
          total={itemStore.donationGridPagination.totalItemCount}
        />
      }
      totalItemCount={itemStore.donationGridPagination.totalItemCount}
      cellSpacing={20}
      onPaginationChange={(pageNumber: number, pageSize: number) =>
        onPaginationChange(pageNumber, pageSize)
      }
      pagination={itemStore.donationGridPagination}
      data={itemStore.donationsGridData}
      headers={columns()}
      name={GridNames.Donation}
      hideTable={isNoData}
      height={
        merchantAccountStore.hasNotificationBar() ? "calc(100vh - 350px)" : ""
      }
      countriesWithState={itemStore.countryStore.countriesWithState}
    />
  );
};

export default inject("donationStore")(withRouter(observer(Grid)));
